import { createHash } from 'node:crypto';
import { existsSync, mkdirSync, readFileSync, renameSync, statSync, writeFileSync } from 'node:fs';
import { dirname, join, resolve as resolvePath } from 'node:path';
import { fileURLToPath } from 'node:url';
import { extractCourseCopy, restoreEnglishCourseCopy, courseCopySourceText, collapseCourseCopyVariants, createCourseCopyKeyResolver } from './copy-contract.js';
import { prepareScriptDisplayCopy, finishScriptDisplayCopy } from './script/localization.js';
import { sharedScriptLocale, sharedScriptLocaleRevision } from './script/shared-copy.js';
import { SnapshotCache } from './snapshot-cache.js';

const PREFIX = '@course-copy:';
const localeCache = new SnapshotCache();
// The English catalogue of a course, read once per course in production (every interface shares it).
const englishCatalogues = new Map();
// Restoring and extracting a document's English copy does not depend on the interface: per English
// catalogue and source document, it is done once and shared by all twelve interfaces.
const extractions = new WeakMap();
const resolvers = new WeakMap();

function read(file) {
  return existsSync(file) ? JSON.parse(readFileSync(file, 'utf8')) : {};
}

function englishCatalogue(root) {
  if (process.env.NODE_ENV === 'development') return read(join(root, 'locales/en.json'));
  const file = join(resolvePath(root), 'locales/en.json');
  if (!englishCatalogues.has(file)) englishCatalogues.set(file, read(file));
  return englishCatalogues.get(file);
}

/**
 * The extraction is also kept on disk (.cache/course-copy), keyed by the source document, its English
 * catalogue and the code that extracts: every prerender worker, test process and the next build reuse
 * it, and any change to the code (which changes the compiled bundle) starts afresh.
 * LL_COURSE_COPY_CACHE=0 turns it off.
 */
const sha = text => createHash('sha256').update(text).digest('hex');
let codeFingerprint;
function extractionCode() {
  if (codeFingerprint === undefined) {
    const here = fileURLToPath(import.meta.url);
    // Bundled: one file holds all the code. From source: the extraction modules next to this one.
    const sources = [here, ...['copy-contract.js', 'copy-extract.js', 'copy-key-resolver.js', 'script/localization.js'].map(name => join(dirname(here), name))];
    codeFingerprint = sha(sources.filter(existsSync).map(file => readFileSync(file, 'utf8')).join('\u0000'));
  }
  return codeFingerprint;
}
const catalogueFingerprints = new WeakMap();
function catalogueFingerprint(englishFile) {
  if (!catalogueFingerprints.has(englishFile)) catalogueFingerprints.set(englishFile, sha(JSON.stringify(englishFile)));
  return catalogueFingerprints.get(englishFile);
}
function diskExtraction(englishFile, value, prefix, id, extract) {
  if (process.env.LL_COURSE_COPY_CACHE === '0') return extract();
  const key = sha([extractionCode(), catalogueFingerprint(englishFile), prefix, id, JSON.stringify(value)].join('\u0000'));
  const file = join(process.cwd(), '.cache/course-copy', key.slice(0, 2), key + '.json');
  try { return JSON.parse(readFileSync(file, 'utf8')); } catch { /* not cached yet */ }
  const result = extract();
  try {
    mkdirSync(dirname(file), { recursive: true });
    const temp = `${file}.${process.pid}.tmp`;
    writeFileSync(temp, JSON.stringify(result));
    renameSync(temp, file);
  } catch { /* the cache is an optimisation only */ }
  return result;
}

function extractedSource(englishFile, value, prefix, id, extract) {
  if (!value || typeof value !== 'object') return extract();
  let byValue = extractions.get(englishFile);
  if (!byValue) extractions.set(englishFile, byValue = new WeakMap());
  let byTrail = byValue.get(value);
  if (!byTrail) byValue.set(value, byTrail = new Map());
  const trail = prefix + '\u0000' + id;
  if (!byTrail.has(trail)) byTrail.set(trail, diskExtraction(englishFile, value, prefix, id, extract));
  return byTrail.get(trail);
}

export function courseLocaleRevision(root, ui, { script = false } = {}) {
  return ['en', ui].map(lang => {
    const file = join(root, 'locales', lang + '.json');
    return existsSync(file) ? file + ':' + statSync(file).mtimeMs : '';
  }).join('|') + (script ? '|' + sharedScriptLocaleRevision(ui) : '');
}

/** Translation fields: what a line, word or example means. */
const GLOSS_KEYS = new Set(['en', 'qEn', 'contextEn', 'b']);

/** A stage line's `en` is no translation: it is the caption of its picture, told in the interface language. */
const glossField = (key, parent) => GLOSS_KEYS.has(key) && !(key === 'en' && parent?.stage === true);

/**
 * How deep in a course document a value sits: `gloss` inside a translation field (or a meaning
 * exercise's options), `meaning` inside a meaning exercise, otherwise `ui`.
 */
function childMode(mode, key, parent) {
  if (mode === 'gloss' || glossField(key, parent)) return 'gloss';
  if (mode === 'meaning' || parent?.kind === 'meaning') return key === 'options' ? 'gloss' : 'meaning';
  return mode;
}

/** Replaces a course document's copy references: translation fields from `glossLayers`, the rest from `layers`. */
function copyFiller(layers, glossLayers) {
  const fill = (item, mode = 'ui') => {
    if (typeof item === 'string' && item.startsWith(PREFIX)) {
      const key = item.slice(PREFIX.length);
      const layer = (mode === 'gloss' ? glossLayers : layers).find(strings => key in strings);
      if (!layer) throw Error('Missing course locale string: ' + key);
      return layer[key];
    }
    if (Array.isArray(item)) return item.map(child => fill(child, mode));
    if (item && typeof item === 'object') return Object.fromEntries(Object.entries(item).map(([key, child]) => [key, fill(child, childMode(mode, key, item))]));
    return item;
  };
  return fill;
}

/** The interface's own translations, and every string a copy key may name (shared script copy included). */
function uiCatalogue(root, ui, english) {
  if (ui === 'en') return { translated: {}, strings: { ...sharedScriptLocale('en').strings, ...english } };
  const translated = read(join(root, 'locales', ui + '.json')).strings || {};
  return { translated, strings: { ...sharedScriptLocale('en').strings, ...english, ...sharedScriptLocale(ui).strings, ...translated } };
}

/**
 * The course's copy in the interface language `ui`. `gloss: 'en'` keeps the translations in
 * English while everything else stays in `ui`: a German interface on the German course would
 * otherwise translate German into German.
 */
export function courseLocale(root, ui = 'en', { gloss = ui } = {}) {
  const cacheKey = JSON.stringify([resolvePath(root), ui, gloss]);
  const cached = localeCache.get(cacheKey);
  if (process.env.NODE_ENV === 'production' && cached) return cached.locale;
  const revision = courseLocaleRevision(root, ui, { script: true });
  if (cached?.revision === revision) return cached.locale;
  const englishFile = englishCatalogue(root);
  const english = englishFile.strings || {};
  const englishGloss = gloss === 'en' && ui !== 'en';
  const { translated, strings } = uiCatalogue(root, ui, english);
  if (!resolvers.has(englishFile)) resolvers.set(englishFile, createCourseCopyKeyResolver(englishFile));
  const resolveCopyKey = resolvers.get(englishFile);
  const resolve = (text, domain = '', context = {}) => {
    if (typeof text !== 'string') return text;
    if (text.startsWith(PREFIX)) {
      const key = text.slice(PREFIX.length);
      if (!(key in strings)) throw Error('Missing course locale string: ' + key);
      return strings[key];
    }
    const source=courseCopySourceText(text,domain,context,englishFile.englishOverrides||{});
    return translated[resolveCopyKey(source, domain, context)] || source;
  };
  const hydrate = value => {
    if (typeof value === 'string') return value.startsWith(PREFIX) ? resolve(value) : value;
    if (Array.isArray(value)) return value.map(hydrate);
    if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, hydrate(item)]));
    return value;
  };
  const hydrateSource = (value, prefix = 'course', id = '') => {
    const { sourceDocument, extractedStrings } = extractedSource(englishFile, value, prefix, id, () => {
      const restored = restoreEnglishCourseCopy(value, english);
      const source = prefix === 'script' ? prepareScriptDisplayCopy(restored) : restored;
      const strings = {};
      const extracted = extractCourseCopy(source, strings, id ? [prefix, id] : [prefix], {englishOverrides:englishFile.englishOverrides||{}, resolveCopyKey});
      return { sourceDocument: extracted, extractedStrings: strings };
    });
    const shared = prefix === 'script' ? { ...sharedScriptLocale('en').strings, ...sharedScriptLocale(ui).strings } : {};
    // The translation wins over shared script copy, then the document's own copy, then the catalogue.
    const layers = [translated, shared, extractedStrings, english];
    const fill = copyFiller(layers, englishGloss ? [extractedStrings, english] : layers);
    const hydrated = collapseCourseCopyVariants(fill(sourceDocument));
    return prefix === 'script' ? finishScriptDisplayCopy(hydrated, ui) : hydrated;
  };
  // Word lookups are translations too.
  const lookups = englishGloss ? courseLocale(root, 'en').lookups : hydrate(englishFile.lookups || {});
  const locale = { hydrate, hydrateSource, resolve, strings, lookups, gloss };
  localeCache.set(cacheKey, { revision, locale });
  return locale;
}
